import React, { useState, useEffect, useContext } from "react";
import DataTableComponent from "react-data-table-component";
import { Chip } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Swal from "sweetalert2";
import IconButton from "@mui/material/IconButton";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

import Templete from "../Templete/Templete";
import AuthService from "../../../services/Admin/AuthService";
import LeadManagementService from "../../../services/Admin/LeadManagementService";
import HttpError from "../../../utill/HttpError";
import LoadingContext from "../../../store/loading-context";
import TxnDetailsDialog from "./TxnDetailsDialog";

const LeadTransactions = () => {
  const loadingCtx = useContext(LoadingContext);

  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [status, setStatus] = useState("");
  const [productId, setProductId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [userId, setUserId] = useState("");
  const [searchText, setSearchText] = useState("");

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  // New states for modal
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchProducts(), fetchCategories()]);
    })();
  }, []);

  const fetchProducts = async () => {
    try {
      loadingCtx.startLoading();
      const token = AuthService.getAccessToken();
      const res = await LeadManagementService.fetchAllProducts(token);
      if (res.status === "SUCCESS") setProducts(res.data || []);
      else Swal.fire("Oops!", res.message, "error");
    } catch (error) {
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const fetchCategories = async () => {
    try {
      loadingCtx.startLoading();
      const token = AuthService.getAccessToken();
      const res = await LeadManagementService.fetchCategories(token);
      if (res.status === "SUCCESS") setCategories(res.data || []);
      else Swal.fire("Oops!", res.message, "error");
    } catch (error) {
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const fetchData = async (p = page, s = size) => {
    try {
      setLoading(true);
      const token = AuthService.getAccessToken();
      const res = await LeadManagementService.leadTransactions(
        token,
        fromDate,
        toDate,
        status,
        categoryId,
        productId,
        userId,
        searchText,
        p,
        s
      );

      if (res.status !== "SUCCESS") throw new Error(res.message);

      setData(res.data?.result || []);
      setCount(res.data?.count || 0);
    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusBg = (s) =>
    ({ SUCCESS: "green", FAILED: "red", REJECTED: "red", PENDING: "#FFCC00" }[
      s
    ] || "grey");

  const stack = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    lineHeight: 1.4,
  };

  const columns = [
    {
      name: "User",
      wrap: true,
      cell: ({ tbl_user, outlet_id }) => (
        <div style={stack}>
          <span>{tbl_user?.user_id || "-"}</span>
          <small>{tbl_user?.name || "-"}</small>
          {outlet_id ? <small>Outlet ID: {outlet_id}</small> : null}
        </div>
      ),
    },
    {
      name: "Order ID",
      wrap: true,
      cell: ({ order_id, sp_order_id }) => (
        <div style={stack}>
          <span>{order_id || "-"}</span>
          <small>{sp_order_id || "-"}</small>
        </div>
      ),
    },
    {
      name: "Product",
      wrap: true,
      cell: ({ tbl_lead_product }) => (
        <div style={stack}>
          <span>{tbl_lead_product?.name || "-"}</span>
          <small>{tbl_lead_product?.tbl_lead_category?.name || "NULL"}</small>
        </div>
      ),
    },
    {
      name: "Customer Details",
      wrap: true,
      cell: ({ customer_name, mobile }) => (
        <div style={stack}>
          <span>{customer_name}</span>
          <small>{mobile}</small>
        </div>
      ),
    },
    {
      name: "Source Details",
      wrap: true,
      cell: ({ source_type, utm_fetch_type }) => (
        <div style={stack}>
          <span>{source_type}</span>
          <small>{utm_fetch_type}</small>
        </div>
      ),
    },
    {
      name: "Created On",
      wrap: true,
      cell: ({ created_on }) => <div style={stack}>{created_on || "-"}</div>,
    },
    {
      name: "Status",
      center: true,
      cell: ({ status }) => (
        <Chip
          size="small"
          color="primary"
          style={{ backgroundColor: statusBg(status) }}
          label={status || "-"}
        />
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <div>
          <IconButton
            color="primary"
            id="basic-button"
            aria-controls={isModalOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={isModalOpen ? "true" : undefined}
            onClick={() => {
              setIsModalOpen(true);
              setSelectedRow(row);
            }}
          >
            <MenuOpenIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    // setSelectedRow(null);
    setIsModalOpen(false);
  };

  const handleOpen = () => {
    // setSelectedRow(null);
    setIsModalOpen(true);
  };

  return (
    <Templete t1="Home" t2="Lead Transactions">
      <section className="content">
        <div className="container-fluid">
          {/* Filters */}
          <div className="card card-primary">
            <div className="card-header">
              <h3 className="card-title">Lead Transactions</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-sm-3">
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-sm-3">
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-sm-3">
                  <select
                    className="form-control"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="SUCCESS">SUCCESS</option>
                    <option value="PENDING">PENDING</option>
                    <option value="REJECTED">REJECTED</option>
                    <option value="FAILED">FAILED</option>
                  </select>
                </div>

                <div className="col-sm-3">
                  <Autocomplete
                    options={products}
                    getOptionLabel={(o) => o?.name || ""}
                    onChange={(e, v) => setProductId(v ? v.id : null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label="Select Product"
                      />
                    )}
                  />
                </div>

                <div className="col-sm-3 mt-2">
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(o) => o?.name || ""}
                    onChange={(e, v) => setCategoryId(v ? v.id : null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label="Select Category"
                      />
                    )}
                  />
                </div>

                <div className="col-sm-3 mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="User ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                </div>

                <div className="col-sm-3 mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>

                <div className="col-sm-3 mt-2 text-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      // reset to first page on new search
                      setPage(1);
                      fetchData(1, size);
                    }}
                  >
                    <i className="fa fa-search" /> Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <DataTableComponent
            noHeader
            columns={columns}
            data={data}
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            pagination
            paginationServer
            paginationTotalRows={count}
            paginationPerPage={size}
            dense
            responsive
            onRowClicked={handleRowClick}
            onChangePage={(newPage) => {
              setPage(newPage);
              fetchData(newPage, size);
            }}
            onChangeRowsPerPage={(newSize) => {
              setSize(newSize);
              setPage(1);
              fetchData(1, newSize);
            }}
            noDataComponent={<div className="p-3">No records found</div>}
          />

          {/* Modal */}
          {isModalOpen && (
            <TxnDetailsDialog
              isModalOpen={isModalOpen}
              handleClose={handleClose}
              handleOpen={handleOpen}
              selectedRow={selectedRow}
            />
          )}
        </div>
      </section>
    </Templete>
  );
};

export default LeadTransactions;
